import * as React from "react";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { Button, cn } from "@finster/ui";
import { Check, FolderSymlink, Search, X } from "lucide-react";
import {
  getUserFiles,
  isQueryableUserFileStatus,
  UserFilesApiError,
  type UserFile,
} from "../api/finster/userFiles";
import { publishUserFiles } from "../api/finster/userFilesStore";

export interface UserFileSelectorTriggerRenderProps {
  open: boolean;
  disabled: boolean;
  selectedFileCount: number;
  triggerRef: React.Ref<HTMLButtonElement>;
  onToggle: () => void;
  onOpen: () => void;
  onClose: () => void;
}

export interface UserFileSelectorProps {
  /** Persisted file ids for the active chat/composer session. */
  selectedFileIds: readonly string[];
  /** Called when the user toggles files or when stale ids are reconciled away. */
  onSelectedFileIdsChange: (fileIds: string[]) => void;
  /** Disables the trigger and closes the picker (e.g. while a query runs). */
  disabled?: boolean;
  /** Optional controlled visibility for hosts that provide a different trigger. */
  open?: boolean;
  /** Called whenever the selector requests an open-state change. */
  onOpenChange?: (open: boolean) => void;
  /** Optional trigger renderer. The default preserves the folder-button UI. */
  renderTrigger?: (props: UserFileSelectorTriggerRenderProps) => React.ReactNode;
  /**
   * When false, opening the panel shows the list state already in hand
   * and does not send another Your Files request.
   */
  refreshOnOpen?: boolean;
  /**
   * "denied" is a 403. "granted" is a successful load.
   * Any other failure leaves the previous access unchanged.
   */
  onYourFilesAccessChange?: (access: "granted" | "denied") => void;
}

const PANEL_WIDTH_PX = 326;
const PANEL_HEIGHT_PX = 270;
const PANEL_GAP_PX = 8;
const VIEWPORT_PADDING_PX = 16;
const FILES_PAGE_SIZE = 4;

function formatCreatedAt(value: string): string {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(date);
}

const YOUR_FILES_ACCESS_DENIED_MESSAGE = "You do not have access to Your Files.";

function getLoadErrorMessage(error: unknown): string {
  if (error instanceof UserFilesApiError && error.status === 403) {
    return YOUR_FILES_ACCESS_DENIED_MESSAGE;
  }
  if (error instanceof Error && error.message.trim()) {
    return error.message;
  }
  return "Unable to load Your Files.";
}

/**
 * Compact picker for the "Your Files" research source.
 *
 * Renders a folder trigger beside the query composer; the panel loads
 * queryable files from the authenticated BFF, supports search and paging.
 * While the panel stays open, refocusing the taskpane triggers a silent
 * refetch so new uploads appear without reopening.
 *
 * Selection state is owned by the host (`selectedFileIds` /
 * `onSelectedFileIdsChange`).
 */
export const UserFileSelector: React.FC<UserFileSelectorProps> = ({
  selectedFileIds,
  onSelectedFileIdsChange,
  disabled = false,
  open: controlledOpen,
  onOpenChange,
  renderTrigger,
  refreshOnOpen = true,
  onYourFilesAccessChange,
}) => {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const requestedOpen = controlledOpen ?? uncontrolledOpen;
  const open = !disabled && requestedOpen;
  if (disabled && uncontrolledOpen) setUncontrolledOpen(false);
  const [files, setFiles] = useState<UserFile[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [pageIndex, setPageIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [panelPosition, setPanelPosition] = useState<{
    side: "top" | "bottom";
    left: number;
    verticalOffset: number;
    width: number;
    height: number;
  }>({
    side: "bottom",
    left: VIEWPORT_PADDING_PX,
    verticalOffset: VIEWPORT_PADDING_PX,
    width: PANEL_WIDTH_PX,
    height: PANEL_HEIGHT_PX,
  });
  const anchorRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const requestControllerRef = useRef<AbortController | null>(null);
  const wasOpenRef = useRef(false);

  const setOpen = useCallback(
    (nextOpen: boolean) => {
      if (nextOpen === open) return;
      if (controlledOpen === undefined) {
        setUncontrolledOpen(nextOpen);
      }
      onOpenChange?.(nextOpen);
    },
    [controlledOpen, onOpenChange, open]
  );

  const selectedFileIdSet = useMemo(() => new Set(selectedFileIds), [selectedFileIds]);
  const queryableFiles = useMemo(
    () => files.filter((file) => isQueryableUserFileStatus(file.status)),
    [files]
  );
  const filteredFiles = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase();
    if (!normalizedQuery) return queryableFiles;
    return queryableFiles.filter((file) => file.name.toLowerCase().includes(normalizedQuery));
  }, [queryableFiles, searchQuery]);
  const pageCount = Math.max(1, Math.ceil(filteredFiles.length / FILES_PAGE_SIZE));
  const safePageIndex = Math.min(pageIndex, pageCount - 1);
  const visibleFiles = useMemo(() => {
    const start = safePageIndex * FILES_PAGE_SIZE;
    return filteredFiles.slice(start, start + FILES_PAGE_SIZE);
  }, [filteredFiles, safePageIndex]);
  const visiblePageNumbers = useMemo(() => {
    if (pageCount <= 3) return Array.from({ length: pageCount }, (_, index) => index);
    const start = Math.min(Math.max(0, safePageIndex - 1), pageCount - 3);
    return [start, start + 1, start + 2];
  }, [pageCount, safePageIndex]);
  const allFilteredSelected =
    filteredFiles.length > 0 &&
    filteredFiles.every((file) => selectedFileIdSet.has(file.id));

  useEffect(() => {
    if (!hasLoaded) return;
    const availableIds = new Set(queryableFiles.map((file) => file.id));
    const reconciledIds = Array.from(
      new Set(selectedFileIds.filter((fileId) => availableIds.has(fileId)))
    );
    const isUnchanged =
      reconciledIds.length === selectedFileIds.length &&
      reconciledIds.every((fileId, index) => fileId === selectedFileIds[index]);
    if (!isUnchanged) {
      onSelectedFileIdsChange(reconciledIds);
    }
  }, [
    hasLoaded,
    onSelectedFileIdsChange,
    queryableFiles,
    selectedFileIds,
  ]);

  const loadFiles = useCallback(async () => {
    requestControllerRef.current?.abort();
    const controller = new AbortController();
    requestControllerRef.current = controller;
    setIsLoading(true);
    setLoadError(null);

    try {
      const nextFiles = await getUserFiles(controller.signal);
      if (controller.signal.aborted) return;
      setFiles(nextFiles);
      publishUserFiles(nextFiles);
      setHasLoaded(true);
      onYourFilesAccessChange?.("granted");
    } catch (error) {
      if (controller.signal.aborted) return;
      setLoadError(getLoadErrorMessage(error));
      if (error instanceof UserFilesApiError && error.status === 403) {
        onYourFilesAccessChange?.("denied");
      }
    } finally {
      if (requestControllerRef.current === controller) {
        requestControllerRef.current = null;
        setIsLoading(false);
      }
    }
  }, [onYourFilesAccessChange]);

  useEffect(() => {
    const didOpen = open && !wasOpenRef.current;
    wasOpenRef.current = open;
    if (didOpen && !isLoading && refreshOnOpen) {
      void loadFiles();
    }
  }, [isLoading, loadFiles, open, refreshOnOpen]);

  // While the picker is open, refetch when the user returns from the
  // manage-files tab (or switches back to the taskpane) so uploads made
  // outside the add-in appear without closing and reopening the menu.
  useEffect(() => {
    if (!open) return undefined;

    const refreshIfVisible = () => {
      if (document.visibilityState !== "visible" || isLoading) return;
      void loadFiles();
    };

    window.addEventListener("focus", refreshIfVisible);
    document.addEventListener("visibilitychange", refreshIfVisible);
    return () => {
      window.removeEventListener("focus", refreshIfVisible);
      document.removeEventListener("visibilitychange", refreshIfVisible);
    };
  }, [isLoading, loadFiles, open]);

  useEffect(
    () => () => {
      const controller = requestControllerRef.current;
      requestControllerRef.current = null;
      controller?.abort();
    },
    []
  );

  const closePanel = useCallback((restoreFocus = false) => {
    setOpen(false);
    if (restoreFocus) {
      window.setTimeout(() => triggerRef.current?.focus(), 0);
    }
  }, [setOpen]);

  useEffect(() => {
    if (disabled && controlledOpen) onOpenChange?.(false);
  }, [controlledOpen, disabled, onOpenChange]);

  useEffect(() => {
    if (!open) return undefined;

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (!anchorRef.current?.contains(target) && !panelRef.current?.contains(target)) {
        closePanel();
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        closePanel(true);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [closePanel, open]);

  useLayoutEffect(() => {
    if (!open) return undefined;

    const updatePanelPosition = () => {
      const triggerRect = anchorRef.current?.getBoundingClientRect();
      if (!triggerRect) return;

      const width = Math.min(
        PANEL_WIDTH_PX,
        Math.max(0, window.innerWidth - VIEWPORT_PADDING_PX * 2)
      );
      const height = Math.min(
        PANEL_HEIGHT_PX,
        Math.max(0, window.innerHeight - VIEWPORT_PADDING_PX * 2)
      );
      const availableBelow =
        window.innerHeight - triggerRect.bottom - PANEL_GAP_PX - VIEWPORT_PADDING_PX;
      const availableAbove = triggerRect.top - PANEL_GAP_PX - VIEWPORT_PADDING_PX;
      const side =
        availableBelow >= height || availableBelow >= availableAbove ? "bottom" : "top";
      const maxLeft = Math.max(
        VIEWPORT_PADDING_PX,
        window.innerWidth - VIEWPORT_PADDING_PX - width
      );

      setPanelPosition({
        side,
        left: Math.min(Math.max(VIEWPORT_PADDING_PX, triggerRect.left - 8), maxLeft),
        verticalOffset:
          side === "top"
            ? window.innerHeight - triggerRect.top + PANEL_GAP_PX
            : triggerRect.bottom + PANEL_GAP_PX,
        width,
        height,
      });
    };

    updatePanelPosition();
    window.addEventListener("resize", updatePanelPosition);
    window.addEventListener("scroll", updatePanelPosition, true);
    return () => {
      window.removeEventListener("resize", updatePanelPosition);
      window.removeEventListener("scroll", updatePanelPosition, true);
    };
  }, [open]);

  const toggleFile = (fileId: string) => {
    onSelectedFileIdsChange(
      selectedFileIdSet.has(fileId)
        ? selectedFileIds.filter((selectedId) => selectedId !== fileId)
        : [...selectedFileIds, fileId]
    );
  };

  const toggleAllFilteredFiles = () => {
    const filteredIds = new Set(filteredFiles.map((file) => file.id));
    if (allFilteredSelected) {
      onSelectedFileIdsChange(
        selectedFileIds.filter((selectedId) => !filteredIds.has(selectedId))
      );
      return;
    }

    onSelectedFileIdsChange([
      ...selectedFileIds,
      ...filteredFiles
        .map((file) => file.id)
        .filter((fileId) => !selectedFileIdSet.has(fileId)),
    ]);
  };

  const renderContent = () => {
    if (isLoading || (!hasLoaded && !loadError)) {
      return (
        <div className="flex flex-1 items-center justify-center px-4 text-xs text-muted-foreground">
          Loading Your Files…
        </div>
      );
    }

    if (loadError) {
      return (
        <div
          className="flex flex-1 flex-col items-center justify-center gap-2 px-4 text-center"
          role="alert"
        >
          <p className="m-0 text-xs text-muted-foreground">{loadError}</p>
          <Button type="button" variant="outline" size="sm" onClick={() => void loadFiles()}>
            Retry
          </Button>
        </div>
      );
    }

    if (queryableFiles.length === 0) {
      return (
        <div className="flex flex-1 flex-col items-center justify-center gap-1 px-4 text-center">
          <p className="m-0 text-xs font-medium text-foreground">
            No files available yet
          </p>
        </div>
      );
    }

    if (filteredFiles.length === 0) {
      return (
        <div className="flex flex-1 flex-col items-center justify-center gap-1 px-4 text-center">
          <p className="m-0 text-xs font-medium text-foreground">
            No files match your search
          </p>
          <p className="m-0 text-xs text-muted-foreground">
            Try another file name.
          </p>
        </div>
      );
    }

    return (
      <div className="flex min-h-0 flex-1 flex-col">
        <div className="min-h-0 flex-1 overflow-y-auto p-1" role="group" aria-label="Your Files">
          {visibleFiles.map((file) => {
            const checked = selectedFileIdSet.has(file.id);
            return (
              <button
                key={file.id}
                type="button"
                role="checkbox"
                aria-checked={checked}
                onClick={() => toggleFile(file.id)}
                className="flex h-9 w-full cursor-pointer items-center gap-1.5 overflow-hidden rounded-md border-0 bg-transparent px-2 text-left hover:bg-accent"
                title={file.name}
              >
                <span
                  className={cn(
                    "flex size-4 min-h-4 min-w-4 items-center justify-center rounded border border-border text-primary",
                    checked && "border-primary bg-primary text-primary-foreground"
                  )}
                  aria-hidden
                >
                  {checked && <Check className="h-3 w-3" />}
                </span>
                <span className="min-w-0 flex-1 truncate text-xs font-medium text-foreground">
                  {file.name}
                </span>
                <span className="shrink-0 text-xs text-muted-foreground">
                  {formatCreatedAt(file.createdAt)}
                </span>
              </button>
            );
          })}
        </div>
        {pageCount > 1 && (
          <div
            className="flex h-10 min-h-10 items-center justify-between border-t border-border px-1"
            aria-label="File pages"
          >
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={safePageIndex === 0}
              onClick={() => setPageIndex((current) => Math.max(0, current - 1))}
              className="h-8 px-2 text-xs"
            >
              Previous
            </Button>
            <div className="flex items-center gap-1">
              {visiblePageNumbers.map((pageNumber) => (
                <Button
                  key={pageNumber}
                  type="button"
                  variant={pageNumber === safePageIndex ? "outline" : "ghost"}
                  size="sm"
                  aria-label={`Page ${pageNumber + 1}`}
                  aria-current={pageNumber === safePageIndex ? "page" : undefined}
                  onClick={() => setPageIndex(pageNumber)}
                  className="h-8 w-8 p-0 text-xs"
                >
                  {pageNumber + 1}
                </Button>
              ))}
            </div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={safePageIndex === pageCount - 1}
              onClick={() =>
                setPageIndex((current) => Math.min(pageCount - 1, current + 1))
              }
              className="h-8 px-2 text-xs"
            >
              Next
            </Button>
          </div>
        )}
      </div>
    );
  };

  const trigger = renderTrigger ? (
    // The renderer forwards this ref to a button; it does not read .current or invoke the event handlers.
    // eslint-disable-next-line react-hooks/refs
    renderTrigger({
      open,
      disabled,
      selectedFileCount: selectedFileIds.length,
      triggerRef,
      onToggle: () => setOpen(!open),
      onOpen: () => setOpen(true),
      onClose: () => closePanel(),
    })
  ) : (
    <Button
      ref={triggerRef}
      type="button"
      variant="outline"
      size="icon"
      onClick={() => setOpen(!open)}
      disabled={disabled}
      aria-label={`Your Files: ${selectedFileIds.length} selected`}
      aria-expanded={open}
      className={cn(
        "h-9 w-9 cursor-pointer rounded-md bg-background hover:border-primary hover:bg-primary/5 hover:text-foreground",
        selectedFileIds.length > 0 && "border-primary bg-primary/5 text-primary"
      )}
    >
      <FolderSymlink className="h-4 w-4" aria-hidden />
    </Button>
  );

  return (
    <div ref={anchorRef} className="relative">
      {trigger}

      {open &&
        createPortal(
          <div
            ref={panelRef}
            role="dialog"
            aria-label="Choose from Your Files"
            className="fixed z-[2147483647] flex flex-col overflow-hidden rounded-lg border border-border bg-background text-foreground shadow-lg"
            style={{
              left: panelPosition.left,
              width: panelPosition.width,
              height: panelPosition.height,
              ...(panelPosition.side === "top"
                ? { bottom: panelPosition.verticalOffset }
                : { top: panelPosition.verticalOffset }),
            }}
          >
            <div className="flex h-9 min-h-9 items-center justify-between border-b border-border px-3">
              <p className="m-0 text-xs font-medium">Choose from Your Files</p>
              <button
                type="button"
                onClick={() => closePanel(true)}
                aria-label="Close Your Files"
                className="flex size-6 cursor-pointer items-center justify-center rounded-md border-0 bg-transparent text-muted-foreground hover:bg-accent hover:text-foreground"
              >
                <X className="h-5 w-5" aria-hidden />
              </button>
            </div>

            <div className="flex h-10 min-h-10 items-center gap-1 p-1">
              <label className="flex h-8 min-w-0 flex-1 items-center gap-1.5 rounded-lg border border-border bg-background px-2 shadow-sm focus-within:border-primary focus-within:ring-1 focus-within:ring-primary-muted">
                <Search className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
                <span className="sr-only">Search your files</span>
                <input
                  type="search"
                  autoFocus
                  value={searchQuery}
                  onChange={(event) => {
                    setSearchQuery(event.target.value);
                    setPageIndex(0);
                  }}
                  placeholder="Search your files"
                  className="h-full min-w-0 flex-1 border-0 bg-transparent p-0 text-xs text-foreground outline-none placeholder:text-muted-foreground"
                />
              </label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={filteredFiles.length === 0 || isLoading || Boolean(loadError)}
                onClick={toggleAllFilteredFiles}
                aria-label={allFilteredSelected ? "Deselect all filtered files" : "Select all filtered files"}
                className="h-8 shrink-0 px-3 text-xs"
              >
                {allFilteredSelected ? "Deselect All" : "Select All"}
              </Button>
            </div>

            {renderContent()}
          </div>,
          document.body
        )}
    </div>
  );
};
